<svelte:options runes={true} />

<script lang="ts">
  import { CommandId, type CommandManager } from '@commands';
  import { entitySpecForClass, type Doc } from '@doc';
  import { Size, size } from '@geom';
  import { localeManager, prefs } from '@reactive';
  import { resourcesState } from '@resources/resourcesLoaded.svelte';
  import { createEntityLayoutSpec, layoutEntity, type SnorfLayout } from '@snorf';
  import { expectDefined } from '@utils';
  import { VisualCache } from '@visual';
  import SnorfLayoutPreview from './SnorfLayoutPreview.svelte';

  // Props
  let {
    doc,
    commandManager,
    selectedUnitClass = $bindable(),
    gridSize = $bindable(size(2, 3)), // width x height (columns x rows); default 2 columns, 3 rows
    previewSize
  }: {
    doc: Doc;
    commandManager: CommandManager;
    selectedUnitClass: string | undefined;
    gridSize?: Size; // determines capacity & layout (columns = width, rows = height)
    previewSize?: Size;
  } = $props();

  // Internal state: list of recent unique class names (most recent first)
  let recents = $state<string[]>([]);

  // Shared cache for previews
  let sharedCache = $state(new VisualCache());

  // Track changes to selectedUnitClass: only add when it's a brand-new unique selection.
  // Existing entries (even if not first) stay in stable order.
  $effect(() => {
    const current = selectedUnitClass;
    if (!current) return;
    if (!doc.classes.hasClass(current)) return;
    if (recents.includes(current)) return; // stable list; no reordering
    const capacity = gridSize.width * gridSize.height;
    let next = [current, ...recents];
    if (next.length > capacity) next = next.slice(0, capacity);
    recents = next;
  });

  // Trim recents when gridSize shrinks
  $effect(() => {
    const capacity = gridSize.width * gridSize.height;
    if (recents.length > capacity) {
      recents = recents.slice(0, capacity);
    }
  });

  // Build preview layouts for recents when resources are loaded
  interface RecentWithLayout {
    className: string;
    layout?: SnorfLayout;
  }
  let recentsWithLayouts = $derived.by<RecentWithLayout[]>(() => {
    if (!resourcesState.resourcesLoaded) {
      return recents.map(className => ({ className, layout: undefined }));
    }
    const colorMode = prefs.colorMode;
    const currentLocale = localeManager.localeId;
    const padding = 2;
    return recents.map(className => {
      try {
        const entitySpec = entitySpecForClass(doc, className);
        const snorfSpec = createEntityLayoutSpec(entitySpec, padding);
        const layout = layoutEntity(
          entitySpec,
          snorfSpec,
          colorMode,
          currentLocale,
          sharedCache,
          { mark: `recent:${className}` }
        );
        return { className, layout };
      } catch (e) {
        console.warn('ClassRecents: failed to build layout for', className, e);
        return { className, layout: undefined };
      }
    });
  });

  // Map recents to grid positions.
  interface PositionedRecent extends RecentWithLayout {
    row: number; // 1-based
    col: number; // 1-based
  }

  // Order: columns filled top-to-bottom, moving right-to-left.
  // Most recent (index 0) -> top of rightmost column.
  let positionedRecents = $derived.by<PositionedRecent[]>(() => {
    const cols = gridSize.width;
    const rows = gridSize.height;
    const capacity = cols * rows;
    return recentsWithLayouts.slice(0, capacity).map((item, index) => {
      const columnFromRight = Math.floor(index / rows); // 0 for rightmost column
      const col = cols - columnFromRight; // 1-based
      const row = (index % rows) + 1; // 1-based
      return { ...item, row, col };
    });
  });

  interface GridSlot {
    row: number;
    col: number;
    item?: PositionedRecent;
    index: number; // 0-based insertion order
  }

  // Build a full grid of slots (capacity) whether or not occupied
  let gridSlots = $derived.by<GridSlot[]>(() => {
    const cols = gridSize.width;
    const rows = gridSize.height;
    const capacity = cols * rows;
    return Array.from({ length: capacity }, (_, index) => {
      const columnFromRight = Math.floor(index / rows);
      const col = cols - columnFromRight;
      const row = (index % rows) + 1;
      const item = positionedRecents[index]; // mapping preserves order
      return { row, col, item, index };
    });
  });

  function handleRecentClick(className: string) {
    selectedUnitClass = className;
    commandManager.dispatch(CommandId.SET_SELECTED_UNITS_CLASS);
  }

  // previewSize now provided as a bindable prop
</script>

<style>
  .class-recents {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
  }
  .recent-button::part(base) {
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .recent-button::part(label) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
  .recent-button.placeholder::part(base) {
    opacity: 0.35;
  }
  /* SnorfLayoutPreview already sizes itself; no additional styling needed */
</style>

<div
  style={`display: grid; grid-auto-flow: row; grid-template-columns: repeat(${gridSize.width}, auto); grid-template-rows: repeat(${gridSize.height}, auto); gap: 4px;`}
  class="class-recents"
  aria-label="Recent classes"
>
  {#each gridSlots as slot (`${slot.row}:${slot.col}`)}
    {#if slot.item}
      {@const item = expectDefined(slot.item)}
      {#key item.className}
        <sl-button
          style={`grid-column: ${slot.col}; grid-row: ${slot.row};`}
          class="recent-button"
          aria-label={item.className}
          onclick={() => handleRecentClick(item.className)}
          onkeydown={(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleRecentClick(item.className);
            }
          }}
          role="button"
          size="small"
          tabindex="0"
        >
          <SnorfLayoutPreview
            cache={sharedCache}
            layout={item.layout}
            mark={`recent:${item.className}`}
            {previewSize}
          />
        </sl-button>
      {/key}
    {:else}
      <sl-button
        style={`grid-column: ${slot.col}; grid-row: ${slot.row};`}
        class="recent-button placeholder"
        aria-label="Empty recent slot"
        disabled
        role="button"
        size="small"
        tabindex="-1"
      >
        <SnorfLayoutPreview
          cache={sharedCache}
          layout={undefined}
          mark={`recent:empty:${slot.index}`}
          {previewSize}
        />
      </sl-button>
    {/if}
  {/each}
</div>
